import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import Skeleton from 'react-loading-skeleton';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Bar, Chart, Line } from 'react-chartjs-2';
import { AggregatedMonth, AggregatedProducts } from '../../../api/types/analytics';
import { decimalToNumber } from '../../../api/analyticsAggregate';
import { formatMoney, formatMonth, formatPercent } from './format';
import { Card, ChartBox, Empty, SectionTitle, Subtle } from './styles';

/** null USD (unknown FX) → gap in the chart instead of a misleading zero. */
const usdPoint = (v: string | null | undefined): number | null => (v === null ? null : decimalToNumber(v));

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  Tooltip,
  Legend,
);

const STAFF_CREDITS_COLOR = '#D99A1E';

const withAlpha = (hex: string, alpha: number) => {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const useChartTheme = () => {
  const theme: any = useTheme();
  return {
    text: theme.palette[700] as string,
    grid: (theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)') as string,
    primary: theme.primary[500] as string,
    success: theme.success as string,
    error: theme.errors[600] as string,
    neutral: (theme.isDark ? '#A7A7A7' : '#858A94') as string,
  };
};

const baseOptions = (c: ReturnType<typeof useChartTheme>): ChartOptions<any> => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { position: 'bottom', labels: { color: c.text, boxWidth: 12 } },
  },
  scales: {
    x: { ticks: { color: c.text }, grid: { color: c.grid } },
    y: { ticks: { color: c.text }, grid: { color: c.grid }, beginAtZero: true },
  },
});

const ChartCard: FC<{ title: string; note?: React.ReactNode; loading: boolean; empty: boolean; children: React.ReactNode }> = ({
  title,
  note,
  loading,
  empty,
  children,
}) => {
  const [t] = useTranslation();
  return (
    <Card>
      <SectionTitle>{title}</SectionTitle>
      {note && <Subtle>{note}</Subtle>}
      <ChartBox>
        {loading ? <Skeleton height={260} /> : empty ? <Empty>{t('DD_NO_DATA')}</Empty> : children}
      </ChartBox>
    </Card>
  );
};

const monthLabels = (months: AggregatedMonth[], partialLabel: string) =>
  months.map((m) => `${m.partial ? `${formatMonth(m.month)} (${partialLabel})` : formatMonth(m.month)}${m.incomplete ? ' *' : ''}`);

export const RevenueChart: FC<{ months: AggregatedMonth[]; loading: boolean }> = ({ months, loading }) => {
  const [t] = useTranslation();
  const c = useChartTheme();
  const data = useMemo(() => {
    const partialAlpha = (color: string) => months.map((m) => withAlpha(color, m.partial ? 0.35 : 0.9));
    return {
      labels: monthLabels(months, t('DD_PARTIAL')),
      datasets: [
        {
          type: 'bar' as const,
          label: t('DD_SERIES_BILLED'),
          data: months.map((m) => usdPoint(m.billed_revenue_usd)),
          backgroundColor: partialAlpha(c.primary),
          borderColor: c.primary,
          borderWidth: months.map((m) => (m.partial ? 1 : 0)),
          borderDash: [4, 2],
          order: 2,
        },
        {
          type: 'bar' as const,
          label: t('DD_SERIES_STAFF_CREDITS'),
          data: months.map((m) => usdPoint(m.staff_credits_net_usd)),
          backgroundColor: partialAlpha(STAFF_CREDITS_COLOR),
          order: 3,
        },
        {
          type: 'line' as const,
          label: t('DD_SERIES_CASH_IN'),
          data: months.map((m) => usdPoint(m.cash_in_usd)),
          borderColor: c.success,
          backgroundColor: c.success,
          tension: 0.25,
          pointRadius: 3,
          segment: {
            borderDash: (ctx: any) => (months[ctx.p1DataIndex]?.partial ? [6, 4] : undefined),
          },
          order: 1,
        },
      ],
    };
  }, [months, c, t]);

  const options = useMemo(() => {
    const o: any = baseOptions(c);
    o.plugins.tooltip = {
      callbacks: {
        label: (ctx: any) =>
          `${ctx.dataset.label}: ${ctx.parsed.y === null ? '—' : formatMoney(String(ctx.parsed.y), 'USD')}`,
      },
    };
    o.scales.y.ticks.callback = (v: any) => formatMoney(String(v), 'USD', true);
    return o;
  }, [c]);

  return (
    <ChartCard
      title={t('DD_CHART_REVENUE')}
      note={<>{t('DD_CHART_REVENUE_NOTE')}{months.some((m) => m.incomplete) && <> {t('DD_USD_INCOMPLETE')}</>}</>}
      loading={loading}
      empty={!months.length}
    >
      <Chart type="bar" data={data as any} options={options} />
    </ChartCard>
  );
};

export const ClientsChart: FC<{ months: AggregatedMonth[]; loading: boolean }> = ({ months, loading }) => {
  const [t] = useTranslation();
  const c = useChartTheme();
  const data = useMemo(
    () => ({
      labels: monthLabels(months, t('DD_PARTIAL')),
      datasets: [
        { label: t('DD_SERIES_NEW'), data: months.map((m) => m.new_clients), backgroundColor: c.primary },
        { label: t('DD_SERIES_NEW_PAYING'), data: months.map((m) => m.new_paying_clients), backgroundColor: c.success },
        { label: t('DD_SERIES_CHURNED'), data: months.map((m) => m.churned_clients), backgroundColor: c.error },
      ],
    }),
    [months, c, t],
  );
  const options = useMemo(() => baseOptions(c), [c]);
  return (
    <ChartCard title={t('DD_CHART_CLIENTS')} loading={loading} empty={!months.length}>
      <Bar data={data} options={options} />
    </ChartCard>
  );
};

export const CohortChart: FC<{ months: AggregatedMonth[]; loading: boolean }> = ({ months, loading }) => {
  const [t] = useTranslation();
  const c = useChartTheme();
  const data = useMemo(
    () => ({
      labels: monthLabels(months, t('DD_PARTIAL')),
      datasets: [
        {
          label: t('DD_SERIES_CONVERSION'),
          data: months.map((m) => decimalToNumber(m.cohort_conversion_rate) * 100),
          borderColor: c.primary,
          backgroundColor: c.primary,
          tension: 0.25,
          pointRadius: 3,
        },
      ],
    }),
    [months, c, t],
  );
  const options = useMemo(() => {
    const o: any = baseOptions(c);
    o.plugins.legend.display = false;
    o.scales.y.suggestedMax = 100;
    o.scales.y.ticks.callback = (v: any) => formatPercent(Number(v), 0);
    o.plugins.tooltip = { callbacks: { label: (ctx: any) => formatPercent(ctx.parsed.y) } };
    return o;
  }, [c]);
  return (
    <ChartCard title={t('DD_CHART_COHORT')} note={t('DD_CHART_COHORT_NOTE')} loading={loading} empty={!months.length}>
      <Line data={data} options={options} />
    </ChartCard>
  );
};

export const ProductsChart: FC<{ products: AggregatedProducts | null; loading: boolean }> = ({ products, loading }) => {
  const [t] = useTranslation();
  const c = useChartTheme();
  const list = useMemo(() => products?.products || [], [products]);
  const data = useMemo(
    () => ({
      labels: list.map((p) => `${p.name}${p.incomplete ? ' *' : ''}`),
      datasets: [
        {
          label: t('DD_SERIES_BILLED'),
          data: list.map((p) => usdPoint(p.billed_revenue_usd)),
          backgroundColor: c.primary,
        },
      ],
    }),
    [list, c, t],
  );
  const options = useMemo(() => {
    const o: any = baseOptions(c);
    o.indexAxis = 'y';
    o.plugins.legend.display = false;
    o.interaction = { mode: 'nearest', axis: 'y', intersect: false };
    o.scales.x.ticks.callback = (v: any) => formatMoney(String(v), 'USD', true);
    o.scales.y.beginAtZero = false;
    o.scales.x.beginAtZero = true;
    o.plugins.tooltip = {
      callbacks: { label: (ctx: any) => formatMoney(list[ctx.dataIndex]?.billed_revenue_usd, 'USD') },
    };
    return o;
  }, [c, list]);

  const note = products ? (
    <>
      {t('DD_PRODUCTS_TOTAL', { value: formatMoney(products.total_usd, 'USD') })}.{' '}
      {t('DD_PRODUCTS_INCLUDED', {
        bareMetal: formatMoney(products.included.bare_metal_usd, 'USD'),
        gpu: formatMoney(products.included.gpu_usd, 'USD'),
      })}
      {products.incomplete && <> {t('DD_USD_INCOMPLETE')}</>}
    </>
  ) : undefined;

  return (
    <ChartCard title={t('DD_CHART_PRODUCTS')} note={note} loading={loading} empty={!list.length}>
      <Bar data={data} options={options} />
    </ChartCard>
  );
};
